import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useNavigate } from "react-router-dom";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
  GridPageChangeEvent,
  GridFilterChangeEvent,
} from "@progress/kendo-react-grid";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import { findAllOrderLabelSplit } from "./services/splitItem.services";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";
import { GrSplits } from "react-icons/gr";
import { formatWeight } from "../../_helper/helper";

const SplitItemHistory: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const gridRef = useRef<any>(null);

  // Redux state
  const loading = useAppSelector((state) => state.splitItem.loading);
  const labelSplitList =
    useAppSelector((state) => state.splitItem.labelSplitDropdownList) || [];
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);

  // Filter state for Kendo Grid
  const [filter, setFilter] = useState<CompositeFilterDescriptor | undefined>(
    undefined,
  );

  useEffect(() => {
    dispatch(findAllOrderLabelSplit());
    return () => {
      dispatch(setCurrentPage(0));
    };
  }, [dispatch]);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    dispatch(setCurrentPage(0));
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className="table-header"
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className="table-cell">
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const filteredData = filter
    ? filterBy(labelSplitList, filter)
    : labelSplitList;

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Split Item List
          </Typography.h4>
          <Button
            type="button"
            fillMode="solid"
            themeColor="primary"
            onClick={() => navigate("create")}
          >
            Split Item
          </Button>
        </GridLayoutItem>

        <GridLayoutItem>
          <KendoGrid
            scrollable="scrollable"
            className="responsive-table"
            style={{ maxHeight: "calc(100vh - 152px)" }}
            filterable={true}
            filter={filter}
            data={filteredData.slice(
              currentPage * pageLimit,
              (currentPage + 1) * pageLimit,
            )}
            total={filteredData.length}
            onFilterChange={handleFilterChange}
            skip={currentPage * pageLimit}
            take={pageLimit}
            pageable={{
              buttonCount: 5,
              pageSizes: [10, 50, 100, 300, 500],
              pageSizeValue: pageLimit,
              type: "input",
            }}
            onPageChange={pageChange}
            cells={{
              headerCell: HeaderCustomCell,
              data: MyDataCustomCell,
            }}
          >
            <Column
              field="item_name"
              title="Item Name"
              filter="text"
              width="200px"
            />
            <Column
              field="UniqueProductID"
              title="Unique Product ID"
              filter="text"
              width="200px"
            />
            <Column
              field="GrossWeight"
              title="Gross Weight (gm)"
              filter="numeric"
              width="180px"
              cells={{
                data: (props: any) => (
                  <td {...props.tdProps} className="table-cell">
                    {formatWeight(props.dataItem.GrossWeight)}
                  </td>
                ),
              }}
            />
            <Column
              field="LessWeight"
              title="Less Weight (gm)"
              filter="numeric"
              width="180px"
              cells={{
                data: (props: any) => (
                  <td {...props.tdProps} className="table-cell">
                    {formatWeight(props.dataItem.LessWeight)}
                  </td>
                ),
              }}
            />
            <Column
              field="NetWeight"
              title="Net Weight (gm)"
              filter="numeric"
              width="180px"
              cells={{
                data: (props: any) => (
                  <td {...props.tdProps} className="table-cell">
                    {formatWeight(props.dataItem.NetWeight)}
                  </td>
                ),
              }}
            />
            <Column
              title="Action"
              width="80px"
              filterable={false}
              cells={{
                data: (props: any) => {
                  const uniqueId =
                    props.dataItem.UniqueProductID ||
                    props.dataItem.unique_product_id ||
                    props.dataItem.id;
                  return (
                    <td {...props.tdProps} className="text-center action-td">
                      <Button
                        type="button"
                        fillMode="flat"
                        themeColor="primary"
                        onClick={() => navigate(`create?id=${uniqueId}`)}
                      >
                        <GrSplits />
                      </Button>
                    </td>
                  );
                },
              }}
            />
          </KendoGrid>
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

export default SplitItemHistory;
